import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { environment } from '@environments/environment';
import { HTTP_INVALID_PERMISSIONS_ERROR } from '@static/constants';
import { INewPortfolio, IPortfolio } from '@static/models/portfolio';
import { HttpService } from '@static/services/http.service';
import { SnackService } from '@static/services/snack.service';
import { StorageService } from '@static/services/storage.service';
import { KEY_PORTFOLIO_SELECTED } from '@static/storage-keys';
import { BehaviorSubject, Subject } from 'rxjs';
import { ProjectService } from './project.service';
import { ToastService } from '@static/services/toast.service';
import { ObservableService } from '@static/services/observable.service';

@Injectable({
  providedIn: 'root',
})
export class PortfolioService {
  private _observableService: ObservableService = new ObservableService();
  private _http: HttpService = inject(HttpService);
  private _snackService: SnackService = inject(SnackService);
  private _storageService: StorageService = inject(StorageService);
  private _projectService: ProjectService = inject(ProjectService);
  private _toastService: ToastService = inject(ToastService);

  public selectedPortfolio: WritableSignal<undefined | number>;
  public portfoliosUpdated: Subject<void>;
  public selectedUpdated: BehaviorSubject<undefined | number>;
  public portfolios: IPortfolio[];

  constructor() {
    this.selectedPortfolio = signal(undefined);
    this.portfoliosUpdated = new Subject<void>();
    this.selectedUpdated = new BehaviorSubject<undefined | number>(undefined);
    this.portfolios = [];

    // Load existing
    const res = this._storageService.loadData(KEY_PORTFOLIO_SELECTED, false);
    if (res) {
      // Load the portfolio
      this.loadPortfolio(Number(res));
    };
  }

  /**
   * Get a portfolio by ID
   * @param id  portfolio ID
   * @returns portfolio if found
   */
  public getPortfolio(id: number): IPortfolio | undefined {
    if (id === undefined) return;
    const res = this.portfolios.filter((p) => !!p && p.id === id);
    return res.length ? res[0] : undefined;
  }

  /**
   * Get the logo for the project or portfolio
   *
   * @param projId  project ID
   * @returns       logo ID
   */
  public getProjectLogo(projId?: string): string | undefined {
    if (projId === undefined) return;
    if (/^p[0-9]+$/i.test(projId)) {
      return this.getPortfolio(Number(projId.slice(1)))?.company_logo;
    } else {
      return this._projectService.getProjectSettings(projId)?.company_logo;
    }
  }

  /**
   * Set the currently selected portfolio
   *
   * @param id  portfolio ID
   * @param update  if the change should be saved to user settings
   */
  public setSelected(id?: number, update = true): void {
    if (id === this.selectedPortfolio()) id = undefined;
    if (id !== undefined) {
      this._projectService.selectedPortfolioName = this.getPortfolio(id)?.name;
    }
    this.selectedPortfolio.set(id);
    if (update) this._storageService.saveData(KEY_PORTFOLIO_SELECTED, id !== undefined ? id.toString() : '', false);
    this.selectedUpdated.next(id);
  }

  /**
   * Get the list of portfolios
   */
  public loadPortfolios(): void {
    const uri = `${environment.backendUri}/portfolios`;
    this._http.getP<IPortfolio[]>(uri)
      .then((res: IPortfolio[]) => {
        if (res) {
          this.portfolios = res;
          const id = this.selectedPortfolio();
          if (id !== undefined) {
            this._projectService.selectedPortfolioName = this.getPortfolio(id)?.name;
          }
        } else {
          this.portfolios = [];
        }
        this.portfoliosUpdated.next();
      })
      .catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
          this._snackService.openErrorSnack('Insufficient permissions for this project', err);
        }
        else this._snackService.openErrorSnack('Failed to get the list of projects', err);
      });
  }

  /**
   * Load and set the portfolio
   *
   * @param id  portfolio ID
   */
  public loadPortfolio(id: number): void {
    const uri = `${environment.backendUri}/portfolio/${id}`;
    this._http.getP<IPortfolio>(uri)
      .then((res: IPortfolio) => {
        if (res) {
          const exists = this.portfolios.findIndex((p) => p.id === res.id);
          if (exists !== -1) {
            this.portfolios[exists] = res;
          } else {
            this.portfolios.push(res);
          }
          this.setSelected(id, false);
          this.portfoliosUpdated.next();
        } else {
          this.setSelected(undefined, true);
        }
      })
      .catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
          this._snackService.openErrorSnack('Insufficient permissions for this project', err);
        }
        else this._snackService.openErrorSnack('Failed to get the list of projects', err);
      });
  }

  /**
   * Create a new portfolio
   *
   * @param data  portfolio data
   */
  public createPortfolio(data: INewPortfolio): void {
    const toastId = this._toastService.addToast('Creating portfolio', undefined, true);
    const uri = `${environment.backendUri}/portfolio`;
    this._http.postP<IPortfolio>(uri, data)
      .then((newPortfolio) => {
        this.loadPortfolios();
        this._projectService.clearAndSetPortfolios(newPortfolio.id, data.projects);
      })
      .catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
          this._snackService.openErrorSnack('Insufficient permissions for this project', err);
        }
        else this._snackService.openErrorSnack('Failed to get the list of projects', err);
      })
      .finally(() => this._toastService.removeToast(toastId));;
  }

  /**
   * Update an existing portfolio
   *
   * @param data  portfolio data
   */
  public updatePortfolio(data: IPortfolio): void {
    const toastId = this._toastService.addToast('Updating portfolio', undefined, true);
    const uri = `${environment.backendUri}/portfolio/${data.id}`;
    this._http.postP<IPortfolio[]>(uri, data)
      .then(() => {
        this.loadPortfolios();
        this._projectService.clearAndSetPortfolios(data.id, data.projects);
      })
      .catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
          this._snackService.openErrorSnack('Insufficient permissions for this project', err);
        }
        else this._snackService.openErrorSnack('Failed to get the list of projects', err);
      })
      .finally(() => this._toastService.removeToast(toastId));;
  }

  /**
   * Delete a portfolio
   *
   * @param id  portfolio ID
   * @returns promise when complete
   */
  public deletePortfolio(id: number): Promise<void> {
    const toastId = this._toastService.addToast('Deleting portfolio', undefined, true);
    const uri = `${environment.backendUri}/portfolio/${id}`;
    return this._http.deleteP<IPortfolio[]>(uri)
      .then(() => {
        if (this.selectedPortfolio() === id) {
          this.setSelected(undefined);
        }
        this.loadPortfolios();
      })
      .catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
          this._snackService.openErrorSnack('Insufficient permissions for this project', err);
        }
        else this._snackService.openErrorSnack('Failed to get the list of projects', err);
      })
      .finally(() => this._toastService.removeToast(toastId));;
  }

  /**
   * Add a project to the portfolio
   *
   * @param portfolioId portfolio ID
   * @param projectId project ID
   * @returns promise when complete
   */
  public addProjectToPortfolio(portfolioId: number, projectId: string): Promise<void> {
    const uri = `${environment.backendUri}/portfolio/${portfolioId}/project/${projectId}/add`;
    return this._http.postP<void>(uri)
      .catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
          this._snackService.openErrorSnack('Insufficient permissions for this project', err);
        }
        else this._snackService.openErrorSnack('Failed to get the list of projects', err);
      });
  }

  /**
   * Remove a project from a portfolio
   *
   * @param portfolioId portfolio ID
   * @param projectId project ID
   * @returns promise when complete
   */
  public removeProjectFromPortfolio(portfolioId: number, projectId: string): Promise<void> {
    const uri = `${environment.backendUri}/portfolio/${portfolioId}/project/${projectId}/delete`;
    return this._http.deleteP<void>(uri)
      .catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
          this._snackService.openErrorSnack('Insufficient permissions for this project', err);
        }
        else this._snackService.openErrorSnack('Failed to get the list of projects', err);
      });
  }

}
